import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AlertItem, AlertType, ComponentModal, MessageModal, Reason, Regulation } from 'src/app/core/models';
import {
  AlertService, ContractGlobalService, ContractService,
  ModalService, ReasonCoreService, ReasonService, RegulationService
} from 'src/app/core/services';
import { ClosedModalError } from 'src/app/core/errors';
import { ReplicateReasonComponent } from '../../replicate-reason/replicate-reason.component';
import { Gb } from 'src/app/core/services/growthbook.service';
import { ReasonCreateOrUpdateV2Component } from '../reason-create-or-update-v2/reason-create-or-update-v2.component';
import { ReasonViewModalV2Component } from '../reason-view-modal-v2/reason-view-modal-v2.component';

interface ContractCard {
  contractId: string;
  contractCode: string;
  contractName: string;
  isOpen: boolean;
  reasons: Reason[];
  reasonsPaged?: Reason[];
  total: number;
}

type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  field: 'code' | 'name' | null;
  direction: SortDirection;
}

@Component({
  selector: 'app-reason-list-v2',
  templateUrl: './reason-list-v2.component.html',
  styleUrls: ['./reason-list-v2.component.sass']
})
export class ReasonListV2Component implements OnInit, OnDestroy {
  public gb: Gb;

  public modelChanged: Subject<string> = new Subject<string>();
  public searchForm: FormGroup;
  public filterForm: FormGroup;
  public contracts = [];
  public regulations: Regulation[] = [];
  public regulationById: { [params: string]: Regulation } = {};
  public params = {};
  public deleteEvent: EventEmitter<any> = new EventEmitter();
  public reason: Reason = new Reason();
  public modalState;
  public contractCards: ContractCard[] = [];
  public contractCardsPaged: ContractCard[] = [];
  public loading = false;
  public showFilter = false;

  public sortState: SortState = { field: null, direction: null };

  public moduleOptions = [
    { value: '', label: 'Todos' },
    { value: 'violation', label: 'Infração' },
    { value: 'traffic', label: 'Passagem' }
  ];

  public typeOptions = [
    { value: '', label: 'Todos' },
    { value: 'técnico', label: 'Técnico' },
    { value: 'não técnico', label: 'Não Técnico' }
  ];

  private globalContractIds: any[] | null = null;
  private _subs: Subscription[] = [];

  get activeReasonService(): ReasonService | ReasonCoreService {
    if (this.gb?.isOn('EM-754')) {
      return this.reasonCoreService;
    }
    return this.reasonService;
  }

  constructor(
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private alertService: AlertService,
    public contractService: ContractService,
    public regulationService: RegulationService,
    public reasonService: ReasonService,
    public reasonCoreService: ReasonCoreService,
    private contractGlobalService: ContractGlobalService
  ) {
    this.modelChanged.pipe(debounceTime(500)).subscribe(() => {
      this.handleSearch();
    });
  }

  async ngOnInit() {
    this.createSearchForm();
    this.createFilterForm();
    this.gb = new Gb();
    await this.gb.init();

    this.globalContractIds = this.contractGlobalService.contracts ?? null;

    this._subs.push(
      this.contractGlobalService.contractEvent.subscribe(async (contracts: any) => {
        this.globalContractIds = contracts ?? null;
        await this.handleSearch();
      })
    );

    this.loading = true;
    await this.getContracts();
    this.loading = false;
    this.contractCardsPaged = [...this.contractCards];
  }

  ngOnDestroy() {
    this._subs.forEach(s => s.unsubscribe());
  }

  createSearchForm() {
    this.searchForm = this.formBuilder.group({
      search: ['']
    });
  }

  createFilterForm() {
    this.filterForm = this.formBuilder.group({
      regulationIds: [[]],
      type: [''],
      module: ['']
    });
  }

  toggleFilter() {
    this.showFilter = !this.showFilter;
  }

  applyFilter() {
    this.handleSearch();
    this.showFilter = false;
  }

  resetFilter() {
    this.filterForm.reset({
      regulationIds: [],
      type: '',
      module: ''
    });
    this.handleSearch();
  }

  openCreateOrUpdateReason(reason = {}) {
    this.modalService.show(new ComponentModal(ReasonCreateOrUpdateV2Component, reason))
      .then(data => {
        const modal = data as any;
        const savedReason = modal?.component?.instance?.reason;
        const i = this.findReasonInCards(savedReason?.id);
        if (i !== null) {
          this.updateReasonInCard(savedReason);
          return;
        }
        this.handleSearch();
      }).catch(err => {
        if (err instanceof ClosedModalError) {
          const modalError = err as ClosedModalError;
          this.modalState = (modalError.modal as ComponentModal).component.instance.lastState;
        } else {
          this.modalState = null;
        }
      });
  }

  openReasonView(reason: Reason) {
    this.modalService.show(new ComponentModal(ReasonViewModalV2Component, reason))
      .catch(err => {
        if (err instanceof ClosedModalError) { }
      });
  }

  openReplicateReason() {
    this.modalService.show(new ComponentModal(ReplicateReasonComponent))
      .then(() => {
        this.params = {};
        this.handleSearch();
      }).catch(err => {
        if (err instanceof ClosedModalError) {
          const modalError = err as ClosedModalError;
          this.modalState = (modalError.modal as ComponentModal).component.instance.lastState;
        } else {
          this.modalState = null;
        }
      });
  }

  async handleSearch() {
    const filterObj: any = {};
    const search = this.searchForm.get('search').value;
    const type = this.filterForm.get('type').value;
    const module = this.filterForm.get('module').value;
    const regulationIds = this.filterForm.get('regulationIds').value;

    if (this.gb?.isOn('EM-754')) {
      if (search != null && search !== '') {
        filterObj.search = `${search}`;
      }
      if (type && type !== '') {
        filterObj.type = type;
      }
      if (module && module !== '') {
        filterObj.applicableModules = module;
      }
      if (regulationIds && regulationIds.length > 0) {
        filterObj.regulationIds = regulationIds.join(',');
      }
    } else {
      if (search != null && search !== '') {
        filterObj['code[contains,or]'] = `${search}`;
        filterObj['name[contains,or]'] = `${search}`;
      }
      if (type && type !== '') {
        filterObj['type[eq]'] = type;
      }
      if (module && module !== '') {
        filterObj['applicableModules[contains]'] = module;
      }
      if (regulationIds && regulationIds.length > 0) {
        filterObj['regulationIds[in]'] = `[${regulationIds.join(',')}]`;
      }
    }

    this.params = filterObj;
    this.loading = true;
    await this.getContracts(this.params);
    this.loading = false;
    this.contractCardsPaged = [...this.contractCards];
  }

  searchKey(text: string) {
    this.modelChanged.next(text);
  }

  async getContracts(params: any = {}) {
    const allContracts = await this.contractService.getAll();
    this.contracts = this.globalContractIds?.length
      ? allContracts.filter(c => this.globalContractIds.includes(c.id))
      : allContracts;

    this.contractCards = this.contracts.map(contract => ({
      contractId: contract.id,
      contractCode: contract.code,
      contractName: contract.name,
      isOpen: false,
      reasons: [],
      total: 0
    }));

    const promises = this.contractCards.map(card =>
      this.getReasonsByContract(card.contractId, params)
    );
    await Promise.all(promises);

    this.contractCards = this.contractCards.filter(c => c.reasons.length > 0);

    await this.loadRegulations(this.contracts);
  }

  async getReasonsByContract(contractId: string, params: any = {}) {
    const reasons = await this.activeReasonService.list({ limit: 1000, contractId, ...params } as any);
    reasons.result.forEach(reason => {
      if (reason.regulationIds != null) {
        this.loadRegulationIds(reason.regulationIds);
      }
    });
    const contractCard = this.contractCards.find(c => c.contractId === contractId);
    if (contractCard) {
      contractCard.reasons = reasons.result;
      contractCard.total = reasons.result.length;
    }
  }

  async loadRegulations(contracts: any[]) {
    const allRegulationIds = new Set<string>();
    this.contractCards.forEach(card => {
      card.reasons.forEach(reason => {
        (reason.regulationIds || []).forEach(id => allRegulationIds.add(id));
      });
    });

    if (allRegulationIds.size === 0) {
      this.regulations = [];
      return;
    }

    const ids = Array.from(allRegulationIds).join(',');
    await this.regulationService.list({ 'id[in]': `[${ids}]` }).then(result => {
      this.regulations = result.result.map(item =>
        Object.assign(new Regulation(), item, { shortDescription: `${item.code} - ${item.shortDescription}` })
      ).sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    });
  }

  loadRegulationIds(regulationIds: string[]) {
    regulationIds.forEach(regulationId => {
      if (this.regulationById[regulationId] == null) {
        this.regulationService.getById(regulationId).then(regulation => {
          this.regulationById[regulationId] = regulation;
        });
      }
    });
  }

  sortBy(field: 'code' | 'name', contractCard: ContractCard) {
    if (this.sortState.field === field) {
      if (this.sortState.direction === 'asc') {
        this.sortState.direction = 'desc';
      } else if (this.sortState.direction === 'desc') {
        this.sortState.direction = null;
        this.sortState.field = null;
        return;
      }
    } else {
      this.sortState.field = field;
      this.sortState.direction = 'asc';
    }

    const dir = this.sortState.direction === 'asc' ? 1 : -1;
    contractCard.reasons = [...contractCard.reasons].sort((a, b) =>
      (a[field] || '').localeCompare(b[field] || '') * dir
    );
  }

  getSortIcon(field: 'code' | 'name'): string {
    if (this.sortState.field !== field) { return 'swap_vert'; }
    return this.sortState.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  findReasonInCards(id: string): ContractCard | null {
    return this.contractCards.find(card => card.reasons.some(r => r.id === id)) || null;
  }

  updateReasonInCard(reason: Reason) {
    const card = this.findReasonInCards(reason?.id);
    if (card) {
      const idx = card.reasons.findIndex(r => r.id === reason.id);
      if (idx > -1) { card.reasons[idx] = reason; }
    }
  }

  updateReasonEdit(reason: Reason) {
    const payload: any = {
      id: reason.id,
      contractId: reason.contractId,
      code: reason.code,
      clientCode: reason.clientCode,
      name: reason.name,
      type: reason.type,
      enabled: reason.enabled,
      regulationIds: reason.regulationIds,
      screen: reason.screen,
      applicableModules: (reason as any).applicableModules,
    };
    return this.activeReasonService.update(payload as Reason)
      .then(() => {
        this.alertService.show(new AlertItem('ReasonSaved', AlertType.success));
      }).catch(() => {
        reason.enabled = !reason.enabled;
        this.alertService.show(new AlertItem('ReasonSaveError', AlertType.danger));
      });
  }

  openDeleteModal(reason: Reason) {
    this.modalService.show(new MessageModal('Remover Motivo', 'Deseja remover este motivo?', true))
      .then(() => {
        this.delete(reason);
      })
      .catch(err => {
        if (err.error && err.error.type === 'reasonNotDelete') {
          this.alertService.show(new AlertItem('ModelNotDelete', AlertType.danger, false, 5000));
        } else {
          if (err.error !== undefined) {
            this.alertService.show(new AlertItem('ReasonDeleteError', AlertType.danger));
          }
        }
      });
  }

  delete(reason: Reason) {
    this.activeReasonService.delete(reason.id)
      .then(() => {
        this.deleteEvent.emit(reason.id);
        this.alertService.show(new AlertItem('ReasonDeleted', AlertType.success));
        this.handleSearch();
      })
      .catch(err => {
        if (err.error && err.error.type === 'reasonNotDelete') {
          this.alertService.show(new AlertItem('ModelNotDelete', AlertType.danger, false, 5000));
        } else {
          this.alertService.show(new AlertItem('ReasonDeleteError', AlertType.danger));
        }
      });
  }

  getModuleLabel(module: string): string {
    switch (module.toLowerCase()) {
      case 'violation': return 'Infração';
      case 'traffic': return 'Passagem';
      default: return module;
    }
  }

  stepModesLabel(txt: string): string {
    switch (txt) {
      case 'audit': return 'Auditoria';
      case 'typing': return 'Digitação';
      case 'quarantine': return 'Quarentena';
      case 'validate': return 'Validação';
      case 'triage': return 'Triagem';
      case 'verifyValid': return 'Verificação Válidas';
      case 'verifyInvalid': return 'Verificação Inválidas';
      case 'exemptPeriod': return 'Período Isento';
      case 'monitoring': return 'Monitoramento CFTV';
      case 'validateCFTV': return 'Validação CFTV';
      case 'verifyCFTV': return 'Verificação CFTV';
      case 'serialNumberConfig': return 'Gerenciar Série';
      case 'preProcessConfig': return 'Configuração Pré-Processamento';
      default: return txt;
    }
  }
}
